import { Close } from '@mos-cat/ds-icons';
import { useTheme } from '@emotion/react';
import {
  forwardRef,
  PropsWithRef,
  ReactNode,
  Ref,
  useEffect,
  useState,
  ComponentProps,
} from 'react';
import {
  Dialog,
  DialogBackdrop,
  Popover,
  PopoverInitialState,
  PopoverProps,
  PopoverDisclosure,
  usePopoverState,
  VisuallyHidden,
} from 'reakit';
import { ResponsiveValue } from 'styled-system';
import { Box } from '../Box/Box';
import { UtilityButton } from '../UtilityButton/UtilityButton';
import { StylesOptions } from '../hooks/useStyles';
import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import { PropsWithAs } from '../as';

export const useModalPopoverState = function (props?: PopoverInitialState) {
  return usePopoverState({ modal: true, ...Object(props) });
};

type ModalPopoverDisclosurePropsExcludeAs = Omit<
  ComponentProps<typeof PopoverDisclosure>,
  'as'
>;
export const ModalPopoverDisclosure = ({
  as,
  ...props
}: PropsWithAs<ModalPopoverDisclosurePropsExcludeAs>) => {
  let [popoverMode, setPopoverMode] = useState(true);
  let theme = useTheme();
  useEffect(
    function () {
      let mq = window.matchMedia(`(min-width: ${theme.breakpoints[0]})`);
      setPopoverMode(mq.matches);
    },
    [props.visible]
  );
  const ariaProps = popoverMode
    ? {}
    : { 'aria-expanded': undefined, 'aria-haspopup': undefined };
  return <PopoverDisclosure as={as} {...ariaProps} {...props} />;
};

type ContentProps = { children: ReactNode } & PopoverProps & StylesOptions;
export type ModalPopoverProps = PropsWithRef<ContentProps>;
/**
 * @deprecated
 */
export const ModalPopover = forwardRef(function (
  { children, styles, ...props }: ContentProps,
  ref: Ref<HTMLDivElement>
) {
  let theme = useTheme();
  let [popoverMode, setPopoverMode] = useState(true);
  useRequiredPropWarning(Boolean(children), 'ModalPopover', 'children');

  useEffect(
    function () {
      let mq = window.matchMedia(`(min-width: ${theme.breakpoints[0]})`);
      setPopoverMode(mq.matches);
    },
    [props.visible]
  );
  return popoverMode ? (
    <Popover {...props} ref={ref} tabIndex={0}>
      <Box
        p={'sp-24'}
        bg="white"
        borderRadius={4}
        border={2}
        borderColor="gray-20"
        boxShadow="0 3px 4px 0 rgba(14,14,15,0.1)"
        styles={styles}
      >
        {children}
      </Box>
    </Popover>
  ) : (
    <DialogBackdrop
      {...props}
      modal
      as={Box}
      zIndex={1010}
      position="fixed"
      top={0}
      left={0}
      size="100%"
      bg={'rgba(0,0,0,.8)' as ResponsiveValue<any>}
      styles={styles}
    >
      <Dialog
        {...props}
        modal
        as={Box}
        bg="white"
        borderRadius={4}
        bottom={0}
        position="fixed"
        width={1}
        p="sp-24"
        pb="sp-16"
      >
        <Box mt="sp-8" mr="sp-8" position="absolute" right={0} top={0}>
          <UtilityButton onClick={props.hide}>
            <Close />
            <VisuallyHidden>Закрыть</VisuallyHidden>
          </UtilityButton>
        </Box>
        {children}
      </Dialog>
    </DialogBackdrop>
  );
});
